import { Tabs, TabsContent, TabsList, TabsTrigger } from '@design-system';
import {
  EmailTab,
  LinkTab,
  FillIntakeFormTab,
  SelfTestIntakeFormTab,
  useInviteClientTrigger,
  type TabType,
} from '@/features/invite-client';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { EdjPilotRestricted } from '@/shared/components/edj-pilot-restricted';
import { useIsEdjPilotAdvisor, useIsEdjPilotAdvisorIntakeForm } from '@/shared/hooks/use-edj-pilot-advisor';

import { ClipboardPen, Link2, Mail } from 'lucide-react';

export function InviteClientTabs() {
  const { flags } = useFeatureFlags();
  const { inviteClientTab, setInviteClientTab } = useInviteClientTrigger();
  const isEdjPilot = useIsEdjPilotAdvisor();
  const isEdjPilotIntakeForm = useIsEdjPilotAdvisorIntakeForm();

  const hideEmailAndFillIntakeForm = flags?.hideInviteEmailAndFillIntakeForm;

  const defaultTab = isEdjPilot ? 'link' : 'email';
  const currentTab = inviteClientTab || defaultTab;

  if (hideEmailAndFillIntakeForm && !isEdjPilot) {
    return (
      <div className="mt-4 w-full">
        <LinkTab withHeader={false} />
      </div>
    );
  }

  const edjAllowedTabs: TabType[] = isEdjPilotIntakeForm
    ? ['link', 'self-test']
    : ['link'];
  const resolvedTab = isEdjPilot && !edjAllowedTabs.includes(currentTab)
    ? 'link'
    : currentTab;

  return (
    <Tabs
      value={resolvedTab}
      onValueChange={value => setInviteClientTab(value as TabType)}
      className="w-full"
    >
      <TabsList className="w-full">
        <EdjPilotRestricted>
          <TabsTrigger
            value="email"
            className="flex-1 gap-2"
          >
            <Mail className="hidden size-4 sm:block" />
            Invite via Email
          </TabsTrigger>
        </EdjPilotRestricted>
        <TabsTrigger
          value="link"
          className="flex-1 gap-2"
        >
          <Link2 className="hidden size-4 sm:block" />
          Share via link
        </TabsTrigger>
        {isEdjPilotIntakeForm ? (
          <TabsTrigger
            value="self-test"
            className="flex-1 gap-2"
          >
            <ClipboardPen className="hidden size-4 sm:block" />
            Plan for Yourself
          </TabsTrigger>
        ) : (
          <EdjPilotRestricted>
            <TabsTrigger
              value="fill-form"
              className="flex-1 gap-2"
            >
              <ClipboardPen className="hidden size-4 sm:block" />
              Fill Intake Form
            </TabsTrigger>
          </EdjPilotRestricted>
        )}
      </TabsList>
      <TabsContent
        value="email"
        className="mt-4"
      >
        <EmailTab />
      </TabsContent>
      <TabsContent
        value="link"
        className="mt-4"
      >
        <LinkTab />
      </TabsContent>
      {isEdjPilotIntakeForm ? (
        <TabsContent
          value="self-test"
          className="mt-4"
        >
          <SelfTestIntakeFormTab />
        </TabsContent>
      ) : (
        <TabsContent
          value="fill-form"
          className="mt-4"
        >
          <FillIntakeFormTab />
        </TabsContent>
      )}
    </Tabs>
  );
}
